import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Stack } from "@fluentui/react";
import { Navigate, Route, Routes } from "react-router-dom";
import GlobalDialogs from "../../../components/app/GlobalDialogs";
import LoadingOverlay from "../../../components/app/LoadingOverlay";
import {
  Socket,
  WebpushListener,
  useWebpush,
} from "../../../matteroomTab/Notification/WebpushContext";
import { WorkspaceActionsProvider } from "../../appShell/components/WorkspaceActionsContext";
import { DEFAULT_WORKSPACE_PAGE, ROUTER_PATH } from "../config/constants";
import { useUnsupportedBrowser } from "../hooks/useUnsupportedBrowser";
import { useWorkspaceBootstrapStore } from "@stores/useWorkspaceBootstrapStore";
import { useNoAccountRoute } from "../hooks/useNoAccountRoute";
import { useAppShell } from "../../appShell/hooks/useAppShell";
import SyncDevice from "../../appShell/components/SyncDevice";
import { lazyWithSuspense } from "../utils/lazy-with-suspense";
import { LoginPageProps } from "@pages/login/page";
import { MainRouters, PublicRoutes } from "../config/routes";
import AuthGate from "./AuthGate";
import AppInitGate from "./AppInitGate";
import WorkspaceShell from "@/features/appShell/components/WorkspaceShell";
import { useUserStore } from "@stores/useUserStore";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getStoredAccessToken } from "../../auth/utils/storage";

const ReportsPage = lazyWithSuspense(() => import("@pages/reports/page"));
const LoginPage = lazyWithSuspense(
  () => import("@pages/login/page"),
) as React.ComponentType<LoginPageProps>;

const UnsupportedBrowserPage = lazyWithSuspense(
  () => import("@components/UnsupportedBrowserPage"),
);

type SetClockingLoadingRef = React.MutableRefObject<
  null | ((loading: boolean) => void)
>;

const ClockingLoadingBridge = ({
  setClockingLoadingRef,
}: {
  setClockingLoadingRef: SetClockingLoadingRef;
}) => {
  const { setClockingLoading } = useWebpush();

  useEffect(() => {
    setClockingLoadingRef.current = setClockingLoading;
    return () => {
      setClockingLoadingRef.current = null;
    };
  }, [setClockingLoading, setClockingLoadingRef]);

  return null;
};

const MatteroomTabContent = () => {
  const unsupportedBrowser = useUnsupportedBrowser();
  const initWorkspaceRef = useRef<null | (() => Promise<void>)>(null);
  const { isWorkspaceInitializing } = useWorkspaceBootstrapStore();
  const { isNoAccountPage, setNoAccountRoute } = useNoAccountRoute();
  const selectedUser = useUserStore((state) => state.selectedUser);
  const isDelegationMode = useUserStore((state) => state.isDelegationMode);
  const setClockingLoadingRef = useRef<null | ((loading: boolean) => void)>(
    null,
  );
 
  const { logined, handleLogin } = useAuth({
    onLoginSuccess: () => initWorkspaceRef.current?.(),
    onNoAccountChange: setNoAccountRoute,
  });

  const {
    initWorkspace,
    loadTimeEntries,
    applyTimeEntryPatch,
    handleWebpushObject,
  } = useAppShell();
  initWorkspaceRef.current = initWorkspace;

  const isAuthed = Boolean(
    logined && getStoredAccessToken(),
  );

  const workspaceActions = useMemo(
    () => ({
      loadTimeEntries,
      applyTimeEntryPatch,
    }),
    [loadTimeEntries, applyTimeEntryPatch],
  );

  const loginElement = (
    <LoginPage 
      handleLogin={handleLogin} 
    />
  );

  const handleWebpushChange = useCallback(
    async (object: any) => {
      await handleWebpushObject(object);

      if (String(object?.action || "").toLowerCase().startsWith("clocking.")) {
        setClockingLoadingRef.current?.(false);
      }
    },
    [handleWebpushObject],
  );

  if (unsupportedBrowser) return <UnsupportedBrowserPage />;

  return (
    <Stack horizontal styles={{ root: { height: "100vh" } }}>
      <SyncDevice />
      <Socket delegateUserId={isDelegationMode ? selectedUser?.email : null} />
      <LoadingOverlay />
      <WebpushListener onChange={handleWebpushChange} />
      <ClockingLoadingBridge setClockingLoadingRef={setClockingLoadingRef} />

      <WorkspaceActionsProvider value={workspaceActions}>
        <Routes>
          {PublicRoutes.map((route) => {
            const Component = route.component;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={<Component />}
              />
            );
          })}

          <Route
            element={<AuthGate isAuthed={isAuthed} fallback={loginElement} />}
          >
            <Route
              element={<AppInitGate isInitializing={isWorkspaceInitializing} />}
            >
              <Route element={<WorkspaceShell />}>
                {MainRouters.map((route) => {
                  const Component = route.component;

                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<Component />}
                    />
                  );
                })}

                <Route
                  key={ROUTER_PATH.reports}
                  path={ROUTER_PATH.reports}
                  element={<ReportsPage />}
                />

                <Route
                  path="*"
                  element={
                    <Navigate
                      to={ROUTER_PATH[DEFAULT_WORKSPACE_PAGE]}
                      replace
                    />
                  }
                />
              </Route>
            </Route>
          </Route>
        </Routes>

        <GlobalDialogs
          isAuthed={isAuthed}
          isNoAccountPage={isNoAccountPage}
        />
      </WorkspaceActionsProvider>
    </Stack>
  );
};

export default MatteroomTabContent;
